import React, { useCallback, useMemo, useState } from 'react';
import { Plus, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  CanvasPopover,
  CanvasPopoverTrigger,
  CanvasPopoverContent,
} from '@/components/ui/canvas-popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { useWorkflowValidationStore } from '@/stores/workflow-validation-store';
import { useNotification } from '@/components/notifications';
import { LoadingSpinner } from '@/components/loading-spinner';
import type { NodeParam, InputValue } from '@/types/node-types';
import { IconTile } from '../../icon-tile';
import { GREY_COLOR } from '@/constants/node-styling';
import {
  isDatasetField,
  fetchCollectionScopeOptions,
  applyRefreshedOptions,
} from './collection-scope-helpers';

const SELECTED_COLOR = '#A855F7'; // purple-500, matches node header

function getOptionId(option: unknown): string {
  if (typeof option === 'string') return option;
  if (typeof option === 'object' && option !== null && 'option_id' in option) {
    return (option as { option_id: string }).option_id;
  }
  return String(option);
}

function getOptionLabel(option: unknown): string {
  if (typeof option === 'string') return option;
  if (
    typeof option === 'object' &&
    option !== null &&
    'option_display_name' in option
  ) {
    return (option as { option_display_name: string }).option_display_name;
  }
  return String(option);
}

interface CollectionScopeCardProps {
  nodeId: string;
  field: NodeParam;
  handleParameterChange: (fieldId: string, value: InputValue) => void;
  isLocked: boolean;
}

export const CollectionScopeCard: React.FC<CollectionScopeCardProps> = ({
  nodeId,
  field,
  handleParameterChange,
  isLocked,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { fieldErrors } = useWorkflowValidationStore();
  const notification = useNotification();

  const isDataset = isDatasetField(field.field_id);
  const entityLabel = isDataset ? 'datasets' : 'collections';

  const hasValidationError = fieldErrors.some(
    (error) => error.nodeId === nodeId && error.fieldId === field.field_id,
  );

  const selectedValues = useMemo(() => {
    if (!Array.isArray(field.value)) return [];
    return (field.value as unknown[]).map((item) => {
      if (typeof item === 'string') return item;
      if (typeof item === 'object' && item !== null && 'option_id' in item) {
        return (item as { option_id: string }).option_id;
      }
      return String(item);
    });
  }, [field.value]);

  const label = useMemo(() => {
    if (selectedValues.length === 0)
      return (
        field.display_name || (isDataset ? 'Dataset scope' : 'Collection scope')
      );
    if (selectedValues.length === 1) {
      const selected = (field.options ?? []).find((opt) => {
        if (typeof opt === 'string') return opt === selectedValues[0];
        if (typeof opt === 'object' && opt !== null && 'option_id' in opt) {
          return (opt as { option_id: string }).option_id === selectedValues[0];
        }
        return false;
      });
      if (
        selected &&
        typeof selected === 'object' &&
        'option_display_name' in selected
      ) {
        return (selected as { option_display_name: string })
          .option_display_name;
      }
      return selectedValues[0];
    }
    return `${selectedValues.length} Selected`;
  }, [selectedValues, field.options, field.display_name, isDataset]);

  const toggleOption = useCallback(
    (optionId: string) => {
      if (!field.options) return;

      const isSelected = selectedValues.includes(optionId);
      let newSelectedIds: string[];

      if (isSelected) {
        newSelectedIds = selectedValues.filter((id) => id !== optionId);
      } else {
        newSelectedIds = [...selectedValues, optionId];
      }

      const selectedObjects = newSelectedIds
        .map((valueId) => {
          const opt = field.options!.find((option) => {
            if (typeof option === 'string') return option === valueId;
            if (
              typeof option === 'object' &&
              option !== null &&
              'option_id' in option
            ) {
              return (option as { option_id: string }).option_id === valueId;
            }
            return false;
          });
          if (opt !== undefined) return opt;

          const existing = Array.isArray(field.value)
            ? (field.value as unknown[]).find((item) => {
                if (typeof item === 'string') return item === valueId;
                if (
                  typeof item === 'object' &&
                  item !== null &&
                  'option_id' in item
                ) {
                  return (item as { option_id: string }).option_id === valueId;
                }
                return false;
              })
            : undefined;
          return existing;
        })
        .filter((item): item is NonNullable<typeof item> => item !== undefined);

      handleParameterChange(field.field_id, selectedObjects as InputValue);
    },
    [selectedValues, field, handleParameterChange],
  );

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const nextOptions = await fetchCollectionScopeOptions(field.field_id);
      applyRefreshedOptions(nodeId, field.field_id, nextOptions);

      if (!nextOptions || (nextOptions as unknown[]).length === 0) {
        notification.warning(`No ${entityLabel} found`);
      } else {
        notification.success('Options refreshed');
      }
    } catch (error) {
      console.error('Failed to refresh options:', error);
      notification.error('Failed to refresh options');
    } finally {
      setIsRefreshing(false);
    }
  }, [field.field_id, nodeId, notification, entityLabel]);

  const iconColor = selectedValues.length > 0 ? SELECTED_COLOR : GREY_COLOR;

  const cardContent = (
    <CanvasPopover open={isOpen} onOpenChange={setIsOpen}>
      <CanvasPopoverTrigger asChild>
        <button
          type='button'
          disabled={isLocked}
          className={cn(
            'nodrag group flex items-center w-full h-full gap-1 pl-2 pr-3 py-1 mb-[6px] rounded-xl relative transition-colors duration-200 bg-muted-background hover:bg-neutral-200 dark:hover:bg-neutral-600 border-2',
            isLocked && 'cursor-not-allowed opacity-60',
            hasValidationError ? 'border-red-500' : 'border-transparent',
          )}
        >
          <IconTile
            icon='FolderClosed'
            backgroundColor={iconColor}
            iconColor='#ffffff'
            size='small'
            iconClassName='!w-5 !h-5'
            style={{
              background: `linear-gradient(to bottom, ${iconColor}, color-mix(in srgb, ${iconColor} 75%, black))`,
            }}
          />
          <span className='text-sm font-medium pl-1 min-w-0 truncate dark:text-white!'>
            {label}
          </span>

          <div className='ml-auto flex items-center gap-2 shrink-0'>
            <span
              className={`text-xs font-normal text-right truncate min-w-0 ${
                hasValidationError
                  ? 'text-red-500'
                  : 'hidden group-hover:block! text-muted-foreground'
              }`}
              style={{
                display: isOpen || hasValidationError ? 'block' : 'none',
              }}
            >
              {hasValidationError ? 'Required' : 'Select...'}
            </span>
            <Plus
              className={`size-4 shrink-0 ${
                hasValidationError
                  ? 'text-red-500'
                  : 'text-muted-foreground hidden group-hover:block!'
              }`}
              style={{
                display: isOpen || hasValidationError ? 'block' : 'none',
              }}
            />
          </div>
        </button>
      </CanvasPopoverTrigger>
      <CanvasPopoverContent
        className='min-w-[280px] w-full rounded-xl p-0 max-w-[340px] overflow-hidden'
        align='center'
        side='bottom'
        sideOffset={10}
      >
        <Command shouldFilter={true}>
          <div className='flex items-center w-full border-b px-3 h-9 gap-2 [&>[data-slot=command-input-wrapper]]:flex-1 [&>[data-slot=command-input-wrapper]]:border-0 [&>[data-slot=command-input-wrapper]]:px-0 [&>[data-slot=command-input-wrapper]]:min-w-0'>
            <CommandInput placeholder={`Search ${entityLabel}...`} />
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRefresh();
                  }}
                  disabled={isRefreshing || isLocked}
                  className='shrink-0 size-8 p-0'
                >
                  {isRefreshing ? (
                    <LoadingSpinner className='size-4' />
                  ) : (
                    <RefreshCw className='size-4' />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>Refresh options</TooltipContent>
            </Tooltip>
          </div>
          <CommandList className='max-h-[200px]'>
            <CommandEmpty>No {entityLabel} found.</CommandEmpty>
            <CommandGroup>
              {(field.options ?? []).map((option) => {
                const optionId = getOptionId(option);
                const optionLabel = getOptionLabel(option);
                const isSelected = selectedValues.includes(optionId);

                return (
                  <CommandItem
                    key={optionId}
                    value={optionLabel}
                    onSelect={() => toggleOption(optionId)}
                    className='cursor-pointer'
                  >
                    <Checkbox
                      checked={isSelected}
                      className='mr-2 pointer-events-none'
                    />
                    <span className='truncate'>{optionLabel}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </CanvasPopoverContent>
    </CanvasPopover>
  );

  if (isLocked) {
    return (
      <div className='px-4 relative'>
        <Tooltip>
          <TooltipTrigger asChild>
            <div>{cardContent}</div>
          </TooltipTrigger>
          <TooltipContent>Workflow is locked</TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return <div className='px-4 relative'>{cardContent}</div>;
};
